import React, { type Dispatch, type SetStateAction } from 'react';
import { useTranslationContext } from '@/context/translation';
import { useIsTablet, useTranslationWithLocale } from '@/hooks';
import { type LandingItem, type LandingTool } from '@/data/aiToolLandings';
import { ToolsGrid } from './ToolsGrid';
import { ToolsRows } from './ToolsRows';
import styles from './ToolsGrid/ToolsGrid.module.scss';

interface TListingProps {
	isTabsSticky: boolean;
	activePositioningId: string | null;
	featuresData: LandingTool[];
	positioningData: LandingItem[];
	activeFeature: LandingTool | null;
	setActiveFeature: Dispatch<SetStateAction<LandingTool | null>>;
	activeTabId: string;
	hoveredTabId: string | null;
	scrollToolsIntoViewIfNeeded: () => void;
	setPinnedFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
}

export const Listing = ({
	isTabsSticky,
	activePositioningId,
	featuresData,
	positioningData,
	activeFeature,
	setActiveFeature,
	activeTabId,
	hoveredTabId,
	scrollToolsIntoViewIfNeeded,
	setPinnedFeatureId,
}: TListingProps) => {
	const isTablet = useIsTablet();
	const { locale, translations } = useTranslationContext();
	const { t, isLoading } = useTranslationWithLocale(
		'landing-config',
		locale,
		translations?.['landing-config'] as Record<string, unknown> | undefined,
	);

	// Check if translations are available (either in state or context)
	const hasTranslations =
		!isLoading ||
		(translations?.['landing-config'] &&
			Object.keys(translations['landing-config']).length > 0);

	// Reserve space for ToolsGrid while translations are loading to prevent layout shift
	// ToolsRows doesn't have this issue because it doesn't use AnimatePresence with dynamic keys
	if (!isTablet && !hasTranslations) {
		// Calculate approximate height: features column + positioning grid
		// Features: ~(featuresData.length * 56px) + positioning: ~(Math.ceil(positioningData.length / 2) * 403px)
		const featuresHeight = featuresData.length * 56; // ~56px per item (24px font + 16px padding * 2 + line-height)
		const positioningRows = Math.ceil(positioningData.length / 2);
		const positioningHeight =
			positioningRows * 403 + (positioningRows - 1) * 40; // 403px per row + 40px gap
		const minHeight = Math.max(featuresHeight, positioningHeight);

		return (
			<div
				className={`${styles['tools-listing']} ${styles['tools-listing_placeholder']}`}
				style={
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					{ '--placeholder-height': `${minHeight}px` } as React.CSSProperties
				}
			>
				<div
					className={`${styles['features']} ${styles['features_placeholder']}`}
				>
					{featuresData.map(item => (
						<div
							key={item.title}
							className={`${styles['features__item']} ${styles['features__item_placeholder']}`}
							aria-hidden='true'
						/>
					))}
				</div>
				<div
					className={`${styles['positioning']} ${styles['positioning_placeholder']}`}
				>
					{positioningData.map(item => (
						<div
							key={item.id}
							className={styles['positioning__item_placeholder']}
							aria-hidden='true'
						/>
					))}
				</div>
			</div>
		);
	}

	return (
		<>
			{isTablet ? (
				<ToolsRows
					activeTabId={activeTabId}
					activeFeature={activeFeature}
					featuresData={featuresData}
					activePositioningId={activePositioningId}
					t={t}
				/>
			) : (
				<ToolsGrid
					isTabsSticky={isTabsSticky}
					activeTabId={activeTabId}
					hoveredTabId={hoveredTabId}
					activeFeature={activeFeature}
					setActiveFeature={setActiveFeature}
					featuresData={featuresData}
					positioningData={positioningData}
					scrollToolsIntoViewIfNeeded={scrollToolsIntoViewIfNeeded}
					setPinnedFeatureId={setPinnedFeatureId}
					t={t}
				/>
			)}
		</>
	);
};
